'use client';

import React, { useEffect, useCallback, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Interfaces } from 'doodle-icons';
import { DesktopProductFrame, PhoneShellFrame } from '@/components/common/MarketingDeviceFrames';

const STEP_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  assign: Interfaces.Checklist,
  submit: Interfaces.Camera,
  grade: Interfaces.Search,
  insights: Interfaces.Analytics,
  bring: Interfaces.Folder,
};

const STEPS = [
  { id: 'assign', frame: 'desktop', stepLabel: 'Step 1', title: 'Assign from your curriculum', subline: 'Pick the concepts you\u2019re teaching. Set a due date. That\u2019s it.', video: '/how-step-2.mp4', feature: 'Curriculum-mapped', fit: 'cover' },
  { id: 'submit', frame: 'phone', stepLabel: 'Step 2', title: 'Students snap and submit', subline: 'Photo of the notebook page \u2192 done. No app install. No new format.', video: '/how-step-3.mp4', feature: 'Frictionless submissions', fit: 'contain' },
  { id: 'grade', frame: 'desktop', stepLabel: 'Step 3', title: 'AI grades with spatial precision', subline: 'Corrections appear on the page where the mistake happened. Every paper graded to your rubric \u2014 consistently.', video: '/how-step-4.mp4', feature: 'Spatial precision', fit: 'cover' },
  { id: 'insights', frame: 'desktop', stepLabel: 'Step 4', title: 'See exactly where students struggle', subline: 'Which formulas break, which concepts slip \u2014 across one student or an entire batch. Group similar gaps for intervention.', video: '/how-step-5.mp4', feature: 'Concept gap detection', fit: 'cover' },
  { id: 'bring', frame: 'desktop', stepLabel: 'Step 5', title: 'Digitize any syllabus in minutes', subline: 'Upload a textbook, worksheet, or PDF. We extract the concept graph and formula relationships automatically.', video: '/how-step-1.mp4', feature: 'Curriculum digitizer', fit: 'cover' },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const HowItWorksCarousel: React.FC = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start', watchDrag: true });
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi]);

  useEffect(() => {
    if (hasEnteredViewport) return;
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '150px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasEnteredViewport]);

  // Auto-play the active slide's video. Inactive slides stay paused so we
  // don't burn bandwidth on five concurrent streams.
  useEffect(() => {
    if (!hasEnteredViewport || prefersReducedMotion()) return;
    videoRefs.current.forEach((vid, idx) => {
      if (!vid) return;
      if (idx === activeIndex) {
        vid.currentTime = 0;
        vid.play().catch(() => {});
      } else {
        vid.pause();
        vid.currentTime = 0;
      }
    });
  }, [activeIndex, hasEnteredViewport]);

  const step = STEPS[activeIndex];
  const Icon = STEP_ICONS[step.id];

  return (
    <div ref={sectionRef} className="hiw-section">
      {/* 2-column: video left, text right */}
      <div className="hiw-columns">
        <div className={`hiw-media-track hiw-media-track--${step.frame}`}>
          <div className="hiw-video-col">
            <div className={`hiw-frame-anchor hiw-frame-anchor--${step.frame}`}>
              <button
                type="button"
                className="hiw-arrow hiw-arrow--prev"
                onClick={scrollPrev}
                disabled={activeIndex === 0}
                aria-label="Previous step"
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button
                type="button"
                className="hiw-arrow hiw-arrow--next"
                onClick={scrollNext}
                disabled={activeIndex === STEPS.length - 1}
                aria-label="Next step"
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
              </button>

              <div className="hiw-carousel-wrap">
                <div ref={emblaRef} className="hiw-viewport">
                  <div className="hiw-container">
                    {STEPS.map((s, idx) => (
                      <div className="hiw-slide" key={s.id}>
                        <div className={`hiw-slide__media hiw-slide__media--${s.frame}`}>
                          {s.frame === 'phone' ? (
                            <PhoneShellFrame className="hiw-phone-frame" mediaClassName="hiw-phone-frame__screen">
                              {s.video && hasEnteredViewport && !prefersReducedMotion() ? (
                                <video
                                  ref={(el) => { videoRefs.current[idx] = el; }}
                                  src={s.video}
                                  muted
                                  loop
                                  playsInline
                                  autoPlay={idx === activeIndex}
                                  preload={idx === activeIndex ? 'auto' : 'none'}
                                  className={`hiw-media hiw-media--${s.fit}`}
                                />
                              ) : (
                                <div className="hiw-media hiw-media--placeholder" aria-hidden="true" />
                              )}
                            </PhoneShellFrame>
                          ) : (
                            <DesktopProductFrame className="hiw-desktop-frame" mediaClassName="hiw-desktop-frame__screen">
                              {s.video && hasEnteredViewport && !prefersReducedMotion() ? (
                                <video
                                  ref={(el) => { videoRefs.current[idx] = el; }}
                                  src={s.video}
                                  muted
                                  loop
                                  playsInline
                                  autoPlay={idx === activeIndex}
                                  preload={idx === activeIndex ? 'auto' : 'none'}
                                  className={`hiw-media hiw-media--${s.fit}`}
                                />
                              ) : (
                                <div className="hiw-media hiw-media--placeholder" aria-hidden="true" />
                              )}
                            </DesktopProductFrame>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="hiw-nav">
                <div className="hiw-dots">
                  {STEPS.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`hiw-dot ${idx === activeIndex ? 'hiw-dot--active' : ''}`}
                      onClick={() => emblaApi?.scrollTo(idx)}
                      aria-label={`Go to step ${idx + 1}`}
                    />
                  ))}
                </div>
                <span className="hiw-counter">Step {activeIndex + 1} of {STEPS.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right col on desktop; splits around video on mobile via display:contents */}
        <div className="hiw-text-col">
          <div className="hiw-text-top">
            <span className="how-step-label__eyebrow hiw-text-col__eyebrow">
              {Icon && <Icon width={18} height={18} className="how-step-icon" aria-hidden />}
              {step.stepLabel} &middot; {step.feature}
            </span>
            <h3 className="how-step-label__title">{step.title}</h3>
          </div>
          <div className="hiw-text-bottom">
            <p className="hiw-desc">{step.subline}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksCarousel;
